// Boss battle — two-phase encounter.
// Phase 1 (45 s): Mythos fires code snippets at arena furniture. Snippets
// trigger 5 s "Exploiting" bars; player patches them (2 s) near the object.
// After 2 patches a WAF powerup spawns — collect it and deploy it on any
// un-shielded item. When only 2 items remain un-shielded, Phase 2 starts.
// Phase 2: Mythos fires 2 snippets/s in all directions. Shield both
// remaining items → win.

import Phaser from 'phaser';
import { brand, brandHex } from '../brand';
import { config } from '../config';
import { GameParams } from '../params';
import { buildArena } from '../systems/arena';
import type { SearchHotspot } from '../systems/arena';
import { createHud, HudHandles, updateAssetChip } from '../systems/hud';
import { createInput, InputAccess } from '../systems/input';
import { SPRITE_KEYS } from '../systems/sprites';
import { RunState } from '../systems/state';
import { track } from '../systems/analytics';
import { AUDIO_KEYS, playBgm, playSfx } from '../systems/audio';
import { AssetType } from '../config';

interface BossTarget {
  hotspot: SearchHotspot;
  glyph: Phaser.GameObjects.Text;
  isExploiting: boolean;
  isExploited: boolean;
  isShielded: boolean;
  exploitObj: { progress: number };
  exploitTween: Phaser.Tweens.Tween | null;
  exploitBarBg: Phaser.GameObjects.Graphics;
  exploitBarFill: Phaser.GameObjects.Graphics;
  wafContainer: Phaser.GameObjects.Container | null;
  patchInProgress: boolean;
  patchObj: { progress: number };
  patchTween: Phaser.Tweens.Tween | null;
  patchBarBg: Phaser.GameObjects.Graphics;
  patchBarFill: Phaser.GameObjects.Graphics;
  /** Red `!` overlay shown when an item enters the exploited (terminal)
   *  state. Cleared on patch / WAF deploy. */
  exploitedMarker: Phaser.GameObjects.Image | null;
  /** Random asset type assigned at boss init — drives the HUD chip icon. */
  assetType: AssetType;
}

const BAR_W = 44;
const BAR_H = 6;

export class BossScene extends Phaser.Scene {
  private params!: GameParams;
  private state!: RunState;
  private hud!: HudHandles;
  private controls!: InputAccess;
  private player!: Phaser.Physics.Arcade.Sprite;
  private boss!: Phaser.GameObjects.Image;
  private bounds!: Phaser.Geom.Rectangle;

  private dashUntil = 0;
  private dashCooldownUntil = 0;
  private iframesUntil = 0;
  private countdownStart = 0;

  private bossTargets: BossTarget[] = [];
  private snippets!: Phaser.Physics.Arcade.Group;
  private reticle: Phaser.GameObjects.Graphics | null = null;
  private reticleTween: Phaser.Tweens.Tween | null = null;

  /** Phase 1 = pre-powerup targeting. Phase 2 = IONIX powerup window
   *  (10 s WAF-on-touch). Phase 3 = post-powerup all-out random fire. */
  private phase: 1 | 2 | 3 = 1;
  private snippetInterval = 0;
  private snippetTimer: Phaser.Time.TimerEvent | null = null;
  private phase3Timer: Phaser.Time.TimerEvent | null = null;
  private phase3Start = 0;
  private phase3IdleTween: Phaser.Tweens.Tween | null = null;

  private patchedCount = 0;
  private shieldedCount = 0;
  private powerupSpawned = false;
  private playerHasPowerup = false;
  /** True from the moment the player first grabs the powerup. Once set, the
   *  pre-fire MYTHOS LOCKING ON / INCOMING EXPLOIT alerts are suppressed —
   *  they were a tutorial cue for the early phase. */
  private powerupClaimed = false;
  private powerupSprite: Phaser.Physics.Arcade.Sprite | null = null;
  /** When the IONIX touch-deploy window expires (epoch ms). 0 when inactive. */
  private powerupExpiresAt = 0;
  private powerupCountdownText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('Boss');
  }

  init(data: { params: GameParams; state: RunState }) {
    this.params = data.params;
    this.state = data.state;
    this.state.outcome = 'pending';

    // Phaser reuses the same Scene instance on scene.start() — class-field
    // defaults only run at construction. Wipe every run-specific field here
    // so a Replay starts clean. Phaser GameObjects + tweens + timers are
    // destroyed by the engine on shutdown, but our refs to them aren't.
    this.bossTargets = [];
    this.dashUntil = 0;
    this.dashCooldownUntil = 0;
    this.iframesUntil = 0;
    this.countdownStart = 0;
    this.reticle = null;
    this.reticleTween = null;
    this.phase = 1;
    this.snippetInterval = 0;
    this.snippetTimer = null;
    this.phase3Timer = null;
    this.phase3Start = 0;
    this.phase3IdleTween = null;
    this.patchedCount = 0;
    this.shieldedCount = 0;
    this.powerupSpawned = false;
    this.playerHasPowerup = false;
    this.powerupClaimed = false;
    this.powerupSprite = null;
    this.powerupExpiresAt = 0;
    this.powerupCountdownText = null;
  }

  create() {
    this.cameras.main.setBackgroundColor(brand.bg);
    const arena = buildArena(this, { withScanner: false });
    this.bounds = arena.bounds;

    // Boss phase doesn't want anything physically blocked by environment
    // items — code snippets need to fly through racks/desks to reach their
    // targets, and the player should be able to weave between furniture.
    // Disable the static bodies that buildArena added (they were for the
    // GameScene CVE-bouncing flow).
    arena.items.children.each((item) => {
      const body = (item as Phaser.Physics.Arcade.Image).body as Phaser.Physics.Arcade.StaticBody | null;
      if (body) body.enable = false;
      return null;
    });

    // player
    this.player = this.physics.add.sprite(
      this.bounds.x + 80,
      this.bounds.y + this.bounds.height / 2,
      SPRITE_KEYS.playerBody,
    );
    this.player.setDisplaySize(40, 40);
    (this.player.body as Phaser.Physics.Arcade.Body).setCircle(14, 6, 12);
    this.player.setDepth(10);
    this.player.setTint(0xffaaaa);
    this.physics.add.collider(this.player, arena.walls);

    // shadow
    const shadow = this.add.ellipse(this.player.x, this.player.y + 14, 30, 8, 0x000000, 0.45).setDepth(9);
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      shadow.setPosition(this.player.x, this.player.y + 14);
    });

    // HUD
    this.hud = createHud(this, {
      name: this.params.name,
      total: arena.hotspots.length,
      healthMax: config.player.healthSegments,
    });
    document.getElementById('hud-overlay')?.classList.add('boss-mode');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.getElementById('hud-overlay')?.classList.remove('boss-mode');
      this.hud.destroy();
      this.controls.destroy();
    });
    this.hud.setHealth(this.state.health, config.player.healthSegments);
    this.hud.setCountdownLabel('ZERO DAY CLOCK');
    this.hud.setAssetsLabel('EXPLOITED');
    this.hud.setCountdown(config.boss.phase1DurationSec);
    // 0 exploited / total → setAssets(remaining=total, total) → text reads "0/total"
    this.hud.setAssets(arena.hotspots.length, arena.hotspots.length);

    this.controls = createInput(this);

    // boss targets — reuse hotspot positions
    this.snippets = this.physics.add.group({ collideWorldBounds: false });
    const types = config.assets.types;
    this.bossTargets = arena.hotspots.map((hs, idx) => {
      const barX = hs.x - BAR_W / 2;
      const barY = hs.y - 36;

      const exploitBarBg = this.add.graphics().setDepth(16);
      exploitBarBg.fillStyle(0x000000, 0.45);
      exploitBarBg.fillRect(barX, barY, BAR_W, BAR_H);
      exploitBarBg.setVisible(false);

      const exploitBarFill = this.add.graphics().setDepth(17);
      exploitBarFill.setVisible(false);

      const patchBarBg = this.add.graphics().setDepth(16);
      patchBarBg.fillStyle(0x000000, 0.45);
      patchBarBg.fillRect(barX, barY, BAR_W, BAR_H);
      patchBarBg.setVisible(false);

      const patchBarFill = this.add.graphics().setDepth(17);
      patchBarFill.setVisible(false);

      // Boss phase doesn't surface '?' markers — items aren't searchable
      // here. The glyph stays empty until a state change ('!', 'P', '✓', 'W').
      const glyph = this.add.text(hs.x, hs.y - 28, '', {
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '10px',
        fontStyle: '700',
        color: brand.primary,
      }).setOrigin(0.5).setDepth(15);

      return {
        hotspot: hs,
        glyph,
        isExploiting: false,
        isExploited: false,
        isShielded: false,
        exploitObj: { progress: 0 },
        exploitTween: null,
        exploitBarBg,
        exploitBarFill,
        wafContainer: null,
        patchInProgress: false,
        patchObj: { progress: 0 },
        patchTween: null,
        patchBarBg,
        patchBarFill,
        exploitedMarker: null,
        assetType: types[Phaser.Math.Between(0, types.length - 1)] as AssetType,
      };
    });

    // Seed the EXPLOITED chips with each target's asset icon. Boss-mode
    // chips show the actual icon at idle and flip red on exploit/clear on
    // patch+WAF — see refreshExploitedCount().
    this.bossTargets.forEach((t, i) => updateAssetChip(i, t.assetType, 'unknown'));

    // boss sprite — enter from off-screen right and land at top-centre.
    // Centring keeps the top-right of the arena clear (no visual collision
    // with the EXPLOIT IN clock in the HUD above) and lets snippets fire
    // outward in all directions to reach every hotspot.
    const W = this.scale.width;
    const bossCenterX = W / 2;
    const bossCenterY = this.bounds.y + 130;
    this.boss = this.add.image(W + 100, bossCenterY, SPRITE_KEYS.bossIdle1)
      .setOrigin(0.5)
      .setDisplaySize(120, 160)
      .setDepth(20);
    this.tweens.add({
      targets: this.boss,
      x: bossCenterX,
      duration: 1100,
      ease: 'Quad.Out',
    });
    this.tweens.add({
      targets: this.boss,
      y: bossCenterY + 8,
      yoyo: true,
      repeat: -1,
      duration: 700,
      ease: 'Sine.InOut',
    });

    // MYTHOS label — pinned above the boss's head. Sprite is 160 tall with
    // origin (0.5, 0.5), so the head is ~80 px above bossCenterY; offset
    // -92 lands just above it.
    const label = this.add.text(bossCenterX, bossCenterY - 92, 'MYTHOS', {
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '16px',
      fontStyle: '700',
      color: brand.threat,
    }).setOrigin(0.5).setDepth(21);
    this.tweens.add({ targets: label, alpha: { from: 0.3, to: 1 }, yoyo: true, repeat: -1, duration: 350 });

    // entry sequence
    this.cameras.main.shake(config.boss.entryShakeMs, 0.012);
    playSfx(this, AUDIO_KEYS.sfxBossIntro);
    playBgm(this, AUDIO_KEYS.bgmBoss);
    this.hud.flashAlert('BOSS INCOMING', brandHex.threat, 1400);

    // player–snippet overlap
    this.physics.add.overlap(
      this.player,
      this.snippets,
      (_, snip) => this.onSnippetHitPlayer(snip as Phaser.Physics.Arcade.Sprite),
    );

    // start shooting after boss entry
    this.snippetInterval = config.boss.snippetIntervalStartMs;
    this.time.delayedCall(1400, () => {
      if (this.state.outcome !== 'pending') return;
      this.countdownStart = this.time.now;
      this.scheduleNextShot();
    });
  }

  update(_t: number, _delta: number) {
    if (this.state.outcome !== 'pending') return;

    // movement
    const speed = this.time.now < this.dashUntil ? config.player.dashSpeed : config.player.speed;
    const ax = this.controls.axisX();
    const ay = this.controls.axisY();
    const len = Math.hypot(ax, ay);
    const nx = len > 0 ? ax / len : 0;
    const ny = len > 0 ? ay / len : 0;
    this.player.setVelocity(nx * speed, ny * speed);

    if (nx < -0.1) this.player.setFlipX(true);
    else if (nx > 0.1) this.player.setFlipX(false);

    const px = Phaser.Math.Clamp(this.player.x, this.bounds.x + 14, this.bounds.right - 14);
    const py = Phaser.Math.Clamp(this.player.y, this.bounds.y + 14, this.bounds.bottom - 14);
    this.player.setPosition(px, py);

    // update snippet positions + proximity checks
    this.updateSnippets();

    // update exploit/patch bar fills every frame
    this.updateBars();

    // IONIX powerup: auto-WAF anything we touch + tick the countdown
    this.updatePowerup();

    // i-frame flicker
    if (this.time.now < this.iframesUntil) {
      const t = Math.floor(this.time.now / 80) % 2 === 0;
      this.player.setAlpha(t ? 0.4 : 1);
    } else if (this.player.alpha !== 1) {
      this.player.setAlpha(1);
    }

    // action button priority: PATCH > GRAB POWERUP > DASH
    // (WAF deploys automatically on touch while the IONIX window is active —
    //  no manual press needed.)
    const patchTarget = this.getNearbyPatchable();
    const nearPowerup = this.powerupSprite && !this.playerHasPowerup
      && Phaser.Math.Distance.Between(this.player.x, this.player.y, this.powerupSprite.x, this.powerupSprite.y) < 40;
    const canDash = this.time.now >= this.dashCooldownUntil;

    if (patchTarget !== null && !this.bossTargets[patchTarget].patchInProgress) {
      this.controls.setActionLabel('PATCH', brandHex.ok);
    } else if (nearPowerup) {
      this.controls.setActionLabel('GRAB', brandHex.asset);
    } else if (this.playerHasPowerup) {
      this.controls.setActionLabel('IONIX', brandHex.asset);
    } else if (canDash) {
      this.controls.setActionLabel('DASH', brandHex.primary);
    } else {
      this.controls.setActionLabel('...', brandHex.textDim);
    }

    if (this.controls.actionPressed()) {
      if (patchTarget !== null && !this.bossTargets[patchTarget].patchInProgress) {
        this.startPatch(patchTarget);
      } else if (nearPowerup && this.powerupSprite) {
        this.grabPowerup();
      } else if (canDash) {
        this.dashUntil = this.time.now + config.player.dashMs;
        this.dashCooldownUntil = this.time.now + config.player.dashCooldownMs;
        this.iframesUntil = this.time.now + config.player.dashMs;
        playSfx(this, AUDIO_KEYS.sfxDash);
        this.add.particles(this.player.x, this.player.y, SPRITE_KEYS.pixel, {
          lifespan: 200, quantity: 12,
          speed: { min: 60, max: 140 },
          tint: brandHex.primaryAlt,
          alpha: { start: 0.7, end: 0 },
          scale: { start: 4, end: 0 },
          blendMode: 'ADD', emitting: false,
        }).explode(12);
      }
    }

    // countdown — phase 1 + phase 2 (powerup window) share the entry clock;
    // phase 3 swaps to its own overrun timer.
    if (this.countdownStart > 0) {
      if (this.phase === 3) {
        const elapsed = (this.time.now - this.phase3Start) / 1000;
        const remaining = Math.max(0, config.boss.phase2DurationSec - elapsed);
        this.hud.setCountdown(Math.ceil(remaining), { warn: true });
        if (remaining <= 0) this.endRun('lost_boss');
      } else {
        const elapsed = (this.time.now - this.countdownStart) / 1000;
        const remaining = Math.max(0, config.boss.phase1DurationSec - elapsed);
        this.hud.setCountdown(Math.ceil(remaining), { warn: remaining <= 10 });
        if (remaining <= 0) this.endRun('lost_boss');
      }
    }
  }

  // -------------------------------------------------------------------------

  private scheduleNextShot() {
    if (this.state.outcome !== 'pending' || this.phase === 3) return;
    this.snippetTimer = this.time.delayedCall(this.snippetInterval, () => {
      if (this.state.outcome !== 'pending' || this.phase === 3) return;
      this.doTargetAndFire();
      // decay interval toward minimum
      this.snippetInterval = Math.max(
        config.boss.snippetIntervalMinMs,
        this.snippetInterval - config.boss.snippetIntervalDecayMs,
      );
      this.scheduleNextShot();
    });
  }

  private doTargetAndFire() {
    // pick a random un-shielded target
    const candidates = this.bossTargets
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => !t.isShielded);
    if (candidates.length === 0) return;
    const { i } = candidates[Phaser.Math.Between(0, candidates.length - 1)];
    const target = this.bossTargets[i];

    // targeting phase: show reticle for 800 ms. Suppress the verbose
    // pre-fire alerts once the player has claimed the IONIX powerup, or
    // once we're in phase 2 — by then the player knows the rhythm and
    // the constant flashing competes with the powerup countdown.
    const showAlerts = !this.powerupClaimed && this.phase === 1;
    this.boss.setTexture(SPRITE_KEYS.bossThrow0);
    this.showReticle(target.hotspot.x, target.hotspot.y);
    if (showAlerts) this.hud.flashAlert('MYTHOS LOCKING ON', brandHex.threat, 800);

    this.time.delayedCall(800, () => {
      if (this.state.outcome !== 'pending') return;
      this.hideReticle();
      // Don't overwrite the phase-3 texture animation if the player's
      // powerup expired during this 800 ms window.
      if (this.phase === 3) return;
      this.boss.setTexture(SPRITE_KEYS.bossThrow1);
      if (showAlerts) this.hud.flashAlert('INCOMING EXPLOIT', brandHex.threat, 600);
      this.fireSnippetAt(i, target.hotspot.x, target.hotspot.y);
      this.time.delayedCall(400, () => {
        if (this.state.outcome !== 'pending' || this.phase === 3) return;
        this.boss.setTexture(SPRITE_KEYS.bossIdle1);
      });
    });
  }

  private fireSnippetAt(targetIdx: number, tx: number, ty: number) {
    const key = Math.random() < 0.5 ? SPRITE_KEYS.codeSnippet0 : SPRITE_KEYS.codeSnippet1;
    // Use group.create() so the body is registered with the group from the
    // start — physics.add.sprite + group.add resets the body's velocity.
    const snip = this.snippets.create(this.boss.x - 30, this.boss.y + 20, key) as Phaser.Physics.Arcade.Sprite;
    snip.setDisplaySize(28, 28);
    snip.setDepth(8);
    const body = snip.body as Phaser.Physics.Arcade.Body;
    body.setCircle(11, 1, 1);
    const angle = Math.atan2(ty - snip.y, tx - snip.x);
    const spd = 280;
    body.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
    snip.setData('birthMs', this.time.now);
    snip.setData('targetIdx', targetIdx);
    this.tweens.add({ targets: snip, angle: 360, duration: 600, repeat: -1 });
  }

  private fireSnippetRandom() {
    const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
    const key = Math.random() < 0.5 ? SPRITE_KEYS.codeSnippet0 : SPRITE_KEYS.codeSnippet1;
    const snip = this.snippets.create(this.boss.x - 30, this.boss.y + 20, key) as Phaser.Physics.Arcade.Sprite;
    snip.setDisplaySize(28, 28);
    snip.setDepth(8);
    const body = snip.body as Phaser.Physics.Arcade.Body;
    body.setCircle(11, 1, 1);
    const spd = 300;
    body.setVelocity(Math.cos(angle) * spd, Math.sin(angle) * spd);
    snip.setData('birthMs', this.time.now);
    snip.setData('targetIdx', -1); // random — check all targets
    this.tweens.add({ targets: snip, angle: 360, duration: 500, repeat: -1 });
  }

  private updateSnippets() {
    const now = this.time.now;
    this.snippets.children.each((obj) => {
      const snip = obj as Phaser.Physics.Arcade.Sprite;
      const body = snip.body as Phaser.Physics.Arcade.Body | null;
      if (!body) return null;

      // bounce off arena walls
      if (snip.x <= this.bounds.x + 12 && body.velocity.x < 0) body.velocity.x = Math.abs(body.velocity.x);
      else if (snip.x >= this.bounds.right - 12 && body.velocity.x > 0) body.velocity.x = -Math.abs(body.velocity.x);
      if (snip.y <= this.bounds.y + 12 && body.velocity.y < 0) body.velocity.y = Math.abs(body.velocity.y);
      else if (snip.y >= this.bounds.bottom - 12 && body.velocity.y > 0) body.velocity.y = -Math.abs(body.velocity.y);

      // expire after 6 s
      if (now - (snip.getData('birthMs') as number) > 6000) {
        snip.destroy();
        return null;
      }

      // proximity check vs. targets
      const targetIdx = snip.getData('targetIdx') as number;
      if (targetIdx >= 0) {
        // aimed at a specific target
        const t = this.bossTargets[targetIdx];
        if (!t.isShielded) {
          const d = Phaser.Math.Distance.Between(snip.x, snip.y, t.hotspot.x, t.hotspot.y);
          if (d < 32) {
            this.onSnippetHitTarget(targetIdx);
            snip.destroy();
          }
        }
      } else {
        // phase 2 random — check all un-shielded targets
        for (let i = 0; i < this.bossTargets.length; i++) {
          const t = this.bossTargets[i];
          if (t.isShielded) continue;
          const d = Phaser.Math.Distance.Between(snip.x, snip.y, t.hotspot.x, t.hotspot.y);
          if (d < 32) {
            this.onSnippetHitTarget(i);
            snip.destroy();
            break;
          }
        }
      }
      return null;
    });
  }

  private updateBars() {
    for (const t of this.bossTargets) {
      if (t.isExploiting || t.isExploited) {
        const barX = t.hotspot.x - BAR_W / 2;
        const barY = t.hotspot.y - 36;
        t.exploitBarFill.clear();
        t.exploitBarFill.fillStyle(brandHex.threat, 1);
        t.exploitBarFill.fillRect(barX, barY, Math.round(BAR_W * t.exploitObj.progress), BAR_H);
        t.exploitBarBg.setVisible(true);
        t.exploitBarFill.setVisible(true);
      } else {
        t.exploitBarBg.setVisible(false);
        t.exploitBarFill.setVisible(false);
      }

      if (t.patchInProgress) {
        const barX = t.hotspot.x - BAR_W / 2;
        const barY = t.hotspot.y - 36;
        t.patchBarFill.clear();
        t.patchBarFill.fillStyle(brandHex.ok, 1);
        t.patchBarFill.fillRect(barX, barY, Math.round(BAR_W * t.patchObj.progress), BAR_H);
        t.patchBarBg.setVisible(true);
        t.patchBarFill.setVisible(true);
      } else {
        t.patchBarBg.setVisible(false);
        t.patchBarFill.setVisible(false);
      }
    }
  }

  private onSnippetHitTarget(i: number) {
    const t = this.bossTargets[i];
    if (t.isShielded) return;

    // impact burst at hotspot
    this.add.particles(t.hotspot.x, t.hotspot.y, SPRITE_KEYS.pixel, {
      lifespan: 300, quantity: 8,
      speed: { min: 50, max: 120 },
      tint: brandHex.threat,
      alpha: { start: 0.8, end: 0 },
      scale: { start: 4, end: 0 },
      blendMode: 'ADD', emitting: false,
    }).explode(8);

    // Phase 3: snippets fire so quickly that the 5 s exploit bar is
    // pointless — every hit immediately exploits the item.
    if (this.phase === 3) {
      this.instantExploit(i);
      return;
    }

    if (t.isExploiting) {
      // restart exploit bar
      t.exploitTween?.remove();
      t.exploitObj.progress = 0;
      this.startExploitTween(i);
    } else if (!t.isExploited) {
      this.startExploit(i);
    }
  }

  /** Skip the exploit bar — terminal exploited state on the next frame.
   *  Used in phase 3, where every snippet that lands is a kill shot. */
  private instantExploit(i: number) {
    const t = this.bossTargets[i];
    if (t.isExploited || t.isShielded) return;
    t.exploitTween?.remove();
    t.exploitTween = null;
    t.isExploiting = false;
    t.isExploited = true;
    t.glyph.setText('!').setColor('#FF5A5A');
    t.hotspot.body.setTint(brandHex.threat);
    if (!t.exploitedMarker) {
      t.exploitedMarker = this.add
        .image(t.hotspot.x + 16, t.hotspot.y - 22, SPRITE_KEYS.exploitedMarker)
        .setOrigin(0.5)
        .setDepth(17);
    }
    this.refreshExploitedCount();
  }

  private startExploit(i: number) {
    const t = this.bossTargets[i];
    t.isExploiting = true;
    t.exploitObj.progress = 0;
    // change hotspot marker glyph to warn + tint the item itself red
    t.glyph.setText('!').setColor('#FF5A5A');
    t.hotspot.body.setTint(brandHex.threat);
    this.startExploitTween(i);
  }

  private startExploitTween(i: number) {
    const t = this.bossTargets[i];
    t.exploitTween = this.tweens.add({
      targets: t.exploitObj,
      progress: 1,
      duration: config.boss.exploitDurationMs,
      ease: 'Linear',
      onComplete: () => {
        t.isExploiting = false;
        t.isExploited = true;
        // Pin a red `!` marker top-right of the item — the design's
        // "exploited badge". Cleared on patch / WAF deploy.
        if (!t.exploitedMarker) {
          t.exploitedMarker = this.add
            .image(t.hotspot.x + 16, t.hotspot.y - 22, SPRITE_KEYS.exploitedMarker)
            .setOrigin(0.5)
            .setDepth(17);
        }
        // Item stays red while in the exploited state — only patch / WAF
        // clears the tint.
        t.hotspot.body.setTint(brandHex.threat);
        this.refreshExploitedCount();
      },
    });
  }

  /** Update the HUD's EXPLOITED N/N counter + each chip's red/clear state. */
  private refreshExploitedCount() {
    this.bossTargets.forEach((t, i) => {
      updateAssetChip(i, t.assetType, t.isExploited ? 'exploited' : 'unknown');
    });
    const exploited = this.bossTargets.filter((t) => t.isExploited).length;
    const total = this.bossTargets.length;
    this.hud.setAssets(total - exploited, total);
  }

  private getNearbyPatchable(): number | null {
    for (let i = 0; i < this.bossTargets.length; i++) {
      const t = this.bossTargets[i];
      if (t.isShielded) continue;
      if (!t.isExploiting && !t.isExploited) continue;
      const d = Phaser.Math.Distance.Between(
        this.player.x, this.player.y, t.hotspot.x, t.hotspot.y,
      );
      if (d < 55) return i;
    }
    return null;
  }

  private getNearbyUnshielded(): number | null {
    for (let i = 0; i < this.bossTargets.length; i++) {
      const t = this.bossTargets[i];
      if (t.isShielded) continue;
      const d = Phaser.Math.Distance.Between(
        this.player.x, this.player.y, t.hotspot.x, t.hotspot.y,
      );
      if (d < 55) return i;
    }
    return null;
  }

  private startPatch(i: number) {
    const t = this.bossTargets[i];
    // cancel exploit
    t.exploitTween?.remove();
    t.isExploiting = false;
    t.patchInProgress = true;
    t.patchObj.progress = 0;
    t.glyph.setText('P').setColor('#00FF88');

    playSfx(this, AUDIO_KEYS.sfxAssetsSecured);

    t.patchTween = this.tweens.add({
      targets: t.patchObj,
      progress: 1,
      duration: config.boss.patchDurationMs,
      ease: 'Linear',
      onComplete: () => {
        t.patchInProgress = false;
        t.isExploited = false;
        t.hotspot.body.clearTint();
        t.exploitedMarker?.destroy();
        t.exploitedMarker = null;
        this.refreshExploitedCount();
        t.glyph.setText('✓').setColor('#00FF88');
        this.patchedCount++;

        if (this.patchedCount >= config.boss.powerupPatchesRequired && !this.powerupSpawned) {
          this.spawnPowerup();
        }
      },
    });
  }

  private spawnPowerup() {
    this.powerupSpawned = true;
    const cx = this.bounds.x + this.bounds.width / 2;
    const cy = this.bounds.y + this.bounds.height / 2;
    this.powerupSprite = this.physics.add.sprite(cx, cy, SPRITE_KEYS.powerupIonix)
      .setDisplaySize(32, 32)
      .setDepth(12);
    // Slow hover, ±8 px from rest, ~3 s round trip — calmer than the
    // pre-existing 700 ms beat which read more like a frantic flicker.
    this.tweens.add({
      targets: this.powerupSprite,
      y: cy - 8,
      yoyo: true, repeat: -1, duration: 1500, ease: 'Sine.InOut',
    });
    // glow ring
    this.add.particles(cx, cy, SPRITE_KEYS.pixel, {
      follow: this.powerupSprite,
      lifespan: 600, speed: 0, quantity: 1, frequency: 80,
      tint: brandHex.asset,
      alpha: { start: 0.5, end: 0 },
      scale: { start: 8, end: 0 },
      blendMode: 'ADD',
    });
    // Persistent banner — stays on screen until grabPowerup() hides it.
    this.hud.showCenterBanner('GRAB THE IONIX POWERUP', brandHex.bgDeep, 0);
  }

  private deployWaf(i: number) {
    const t = this.bossTargets[i];
    if (t.isShielded) return;
    t.isShielded = true;
    t.isExploiting = false;
    t.isExploited = false;
    t.hotspot.body.clearTint();
    t.exploitedMarker?.destroy();
    t.exploitedMarker = null;
    t.exploitTween?.remove();
    t.patchTween?.remove();
    t.patchInProgress = false;
    // IONIX powerup persists for the whole window — don't clear it here.

    // WAF bar visual above the hotspot marker
    const barW = BAR_W + 4;
    const barX = t.hotspot.x - barW / 2;
    const barY = t.hotspot.y - 38;
    const g = this.add.graphics().setDepth(18);
    g.fillStyle(brandHex.primary, 1);
    g.fillRoundedRect(barX, barY, barW, 12, 3);
    const label = this.add.text(t.hotspot.x, barY + 6, 'WAF', {
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '7px',
      fontStyle: 'bold',
      color: '#FFFFFF',
    }).setOrigin(0.5).setDepth(19);
    t.wafContainer = this.add.container(0, 0, [g, label]).setDepth(18);
    t.glyph.setText('W').setColor(`#${brandHex.primary.toString(16).padStart(6, '0')}`);

    this.shieldedCount++;
    // WAF deploy clears the exploited flag — keep the EXPLOITED counter in sync.
    this.refreshExploitedCount();
    playSfx(this, AUDIO_KEYS.sfxAssetsSecured);
    this.hud.flashAlert('WAF DEPLOYED!', brandHex.primary, 800);
    track('waf_deployed', { phase: this.phase, shieldedCount: this.shieldedCount });

    // Win check fires regardless of phase — shielding the last item ends
    // the run as 'boss-win'. Phase 3 has no other exit besides this or
    // health hitting zero.
    this.checkWinCondition();
  }

  /**
   * Hero grabbed the IONIX powerup → Phase 2. Opens a 10-second window
   * during which any unshielded boss target the hero brushes against
   * gets a WAF auto-deployed. A countdown sits above the hero's head.
   * Phase 3 fires when this timer hits zero.
   */
  private grabPowerup() {
    if (!this.powerupSprite) return;
    this.phase = 2;
    this.playerHasPowerup = true;
    this.powerupClaimed = true;
    this.powerupExpiresAt = this.time.now + config.boss.powerupActiveMs;
    this.powerupSprite.destroy();
    this.powerupSprite = null;

    const startSecs = Math.ceil(config.boss.powerupActiveMs / 1000);
    this.powerupCountdownText = this.add.text(this.player.x, this.player.y - 36, String(startSecs), {
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: '700',
      color: brand.primary,
      stroke: '#0E204F',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(22);

    // Drop the persistent GRAB cue first, then flash the brief confirm.
    this.hud.hideCenterBanner();
    this.hud.showCenterBanner('IONIX POWER UP', brandHex.bgDeep, 1500);
    playSfx(this, AUDIO_KEYS.sfxAssetsSecured);
  }

  /**
   * Per-frame: while the powerup window is active, pin the countdown above
   * the hero, auto-deploy WAF on touch, and end the window when the timer
   * expires.
   */
  private updatePowerup() {
    if (!this.playerHasPowerup) return;
    const remainingMs = this.powerupExpiresAt - this.time.now;

    // Touch-deploy: any unshielded target within touch radius gets a WAF.
    for (let i = 0; i < this.bossTargets.length; i++) {
      const t = this.bossTargets[i];
      if (t.isShielded) continue;
      const d = Phaser.Math.Distance.Between(
        this.player.x, this.player.y, t.hotspot.x, t.hotspot.y,
      );
      if (d < config.boss.powerupTouchRadius) this.deployWaf(i);
    }

    if (this.powerupCountdownText) {
      this.powerupCountdownText.setPosition(this.player.x, this.player.y - 36);
      const secs = Math.max(0, Math.ceil(remainingMs / 1000));
      if (this.powerupCountdownText.text !== String(secs)) {
        this.powerupCountdownText.setText(String(secs));
      }
    }

    if (remainingMs <= 0) {
      this.playerHasPowerup = false;
      this.powerupExpiresAt = 0;
      if (this.powerupCountdownText) {
        const txt = this.powerupCountdownText;
        this.tweens.add({
          targets: txt, alpha: 0, duration: 200,
          onComplete: () => txt.destroy(),
        });
        this.powerupCountdownText = null;
      }
      // Powerup expiry → Phase 3 (the all-out finale).
      this.startPhase3();
    }
  }

  /** Phase 3 = the post-powerup all-out finale. Triggered exclusively from
   *  the IONIX powerup expiry path in updatePowerup(). The boss texture
   *  swaps to the dedicated phase-3 frames and snippet fire goes random. */
  private startPhase3() {
    if (this.phase === 3) return;
    this.phase = 3;
    this.snippetTimer?.remove();
    this.phase3Start = this.time.now;

    // alternate the two phase-3 frames as a low-frequency idle so the boss
    // visibly "ticks" through the finale instead of holding a static pose
    this.boss.setTexture(SPRITE_KEYS.bossPhase3_1);
    this.phase3IdleTween?.remove();
    this.phase3IdleTween = this.tweens.add({
      targets: { v: 0 },
      v: 1,
      duration: 320,
      yoyo: true,
      repeat: -1,
      onYoyo: () => this.boss.setTexture(SPRITE_KEYS.bossPhase3_2),
      onRepeat: () => this.boss.setTexture(SPRITE_KEYS.bossPhase3_1),
    });

    this.cameras.main.shake(400, 0.01);
    this.hud.setCountdownLabel('OVERRUN CLOCK');
    track('boss_phase3', {});

    // fire 2 snippets per second in random directions
    const interval = Math.round(1000 / config.boss.phase2SnippetsPerSec);
    this.phase3Timer = this.time.addEvent({
      delay: interval,
      loop: true,
      callback: () => {
        if (this.state.outcome !== 'pending') return;
        this.fireSnippetRandom();
        this.fireSnippetRandom();
      },
    });
  }

  private checkWinCondition() {
    const allShielded = this.bossTargets.every((t) => t.isShielded);
    if (allShielded) {
      this.endRun('boss-win');
    }
  }

  private showReticle(tx: number, ty: number) {
    this.hideReticle();
    const g = this.add.graphics().setDepth(18);
    g.lineStyle(2, brandHex.threat, 1);
    g.strokeCircle(tx, ty, 20);
    // crosshairs
    g.lineBetween(tx - 28, ty, tx - 14, ty);
    g.lineBetween(tx + 14, ty, tx + 28, ty);
    g.lineBetween(tx, ty - 28, tx, ty - 14);
    g.lineBetween(tx, ty + 14, tx, ty + 28);
    this.reticle = g;
    this.reticleTween = this.tweens.add({
      targets: g, alpha: { from: 0.3, to: 1 }, yoyo: true, repeat: -1, duration: 200,
    });
  }

  private hideReticle() {
    this.reticleTween?.remove();
    this.reticle?.destroy();
    this.reticle = null;
  }

  private onSnippetHitPlayer(snip: Phaser.Physics.Arcade.Sprite) {
    if (this.time.now < this.iframesUntil) return;
    this.iframesUntil = this.time.now + config.player.iframeMs;
    this.state.health = Math.max(0, this.state.health - 1);
    this.state.cveHits++;
    this.hud.setHealth(this.state.health, config.player.healthSegments);
    playSfx(this, AUDIO_KEYS.sfxCveHit);
    track('snippet_hit', { remainingHealth: this.state.health, phase: this.phase });

    this.add.particles(snip.x, snip.y, SPRITE_KEYS.pixel, {
      lifespan: 350, quantity: 14,
      speed: { min: 90, max: 220 },
      tint: brandHex.threat,
      alpha: { start: 0.9, end: 0 },
      scale: { start: 5, end: 0 },
      blendMode: 'ADD', emitting: false,
    }).explode(14);

    snip.destroy();
    this.cameras.main.shake(160, 0.006);
    this.player.setTintFill(0xffffff);
    this.time.delayedCall(80, () => this.player.clearTint());

    if (this.state.health <= 0) this.endRun('lost_health');
  }

  private endRun(outcome: 'boss-win' | 'lost_boss' | 'lost_health') {
    if (this.state.outcome !== 'pending') return;
    this.state.outcome = outcome === 'boss-win' ? 'boss-win' : 'win-then-boss';
    this.state.endTimeMs = this.time.now;
    this.snippetTimer?.remove();
    this.phase3Timer?.remove();
    this.phase3IdleTween?.remove();

    if (outcome === 'boss-win') {
      track('boss_win', { shieldedCount: this.shieldedCount });
      playSfx(this, AUDIO_KEYS.sfxAssetsSecured);
      this.hud.flashAlert('YOU STOPPED MYTHOS!', brandHex.ok, 2000);
    } else {
      track(outcome, { shieldedCount: this.shieldedCount });
      // sfxLose plays in EndScene as a stinger; here it would collide with
      // sfxCveHit on the killing blow.
      this.hud.flashAlert(outcome === 'lost_health' ? 'COMPROMISED' : 'OVERRUN', brandHex.threat, 1500);
    }
    this.time.delayedCall(1800, () => this.scene.start('End', { params: this.params, state: this.state }));
  }
}
